import { use, useCallback } from "react";
import { TaskSelectionContext } from "./TaskSelectionContext";

/**
 * Returns a stable `getTargetIds(clickedId)` for selection-aware bulk edits.
 * If the clicked row is part of a multi-selection, all selected ids are
 * returned; otherwise just the clicked id. Reads the selection store
 * imperatively so callers don't re-render on selection changes.
 */
export function useBulkTargetIds(): (clickedId: number) => number[] {
    const store = use(TaskSelectionContext);
    if (!store) {
        throw new Error("useBulkTargetIds must be used within a TaskSelectionProvider");
    }
    return useCallback(
        (clickedId: number): number[] => {
            const { selectedTaskIds } = store.getState();
            if (!selectedTaskIds[clickedId]) {
                return [clickedId];
            }
            const ids = Object.entries(selectedTaskIds)
                .filter(([, isSelected]) => isSelected)
                .map(([id]) => Number(id));
            return ids.length > 1 ? ids : [clickedId];
        },
        [store]
    );
}
